import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { meApi } from '../api/endpoints.js';
import { AuthenticatedImage } from '../components/AuthenticatedImage.jsx';
import { ScreenshotLightbox } from '../components/ScreenshotLightbox.jsx';
import { EmployeeHeader } from '../components/EmployeeHeader.jsx';

/**
 * Employee self-view of their own screenshots — transparency page.
 * Same shape as the admin Screenshots section on AdminEmployeeDetailPage,
 * but standalone, scoped to req.user (server-side), and without the
 * "viewed by me" dot since self-views aren't tracked.
 */

const RANGE_PRESETS = [
    { key: 'today',     label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last_7',    label: 'Last 7 days' },
];

function computeRange(preset) {
    const today = DateTime.now().setZone('Asia/Karachi').startOf('day');
    if (preset === 'today')     return { start: today.toISODate(), end: today.toISODate() };
    if (preset === 'yesterday') {
        const y = today.minus({ days: 1 });
        return { start: y.toISODate(), end: y.toISODate() };
    }
    if (preset === 'last_7')    return { start: today.minus({ days: 6 }).toISODate(),  end: today.toISODate() };
    return { start: today.toISODate(), end: today.toISODate() };
}

export default function MyScreenshotsPage() {
    const [preset, setPreset] = useState('today');
    const [lightbox, setLightbox] = useState({ open: false, flatIndex: 0 });

    const { start, end } = useMemo(() => computeRange(preset), [preset]);

    const query = useQuery({
        queryKey: ['me', 'screenshots', start, end],
        queryFn: () => meApi.getMyScreenshots({ start, end }),
        enabled: !!start && !!end,
        refetchInterval: 60_000,
    });

    const flat = useMemo(() => {
        if (!query.data?.days) return [];
        return query.data.days.flatMap((d) => d.screenshots);
    }, [query.data]);

    const openAt = (id) => {
        const idx = flat.findIndex((s) => s.id === id);
        if (idx >= 0) setLightbox({ open: true, flatIndex: idx });
    };

    return (
        <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 20px' }}>
            <EmployeeHeader />

            <div className="flex items-center" style={{ gap: 8, fontSize: 13, marginBottom: 14 }}>
                <Link to="/employee" style={{ color: 'var(--color-text-tertiary)', textDecoration: 'none' }}>
                    Dashboard
                </Link>
                <span style={{ color: 'var(--color-text-tertiary)' }}>/</span>
                <span>My screenshots</span>
            </div>

            <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>My screenshots</h1>
            <p
                style={{
                    fontSize: 13,
                    color: 'var(--color-text-secondary)',
                    marginTop: 6,
                    marginBottom: 18,
                    lineHeight: 1.5,
                    maxWidth: 640,
                }}
            >
                The desktop agent captures a screenshot at a random moment within
                every 10-minute window while you are checked in. Captures are
                stored privately and visible to administrators / your department
                manager. This page shows what has been recorded about you.
            </p>

            <div className="surface-card" style={{ padding: 18, marginBottom: 18 }}>
                <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                    {RANGE_PRESETS.map((p) => (
                        <RangeChip
                            key={p.key}
                            active={preset === p.key}
                            onClick={() => setPreset(p.key)}
                        >
                            {p.label}
                        </RangeChip>
                    ))}
                    {query.isSuccess && (
                        <span style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: 'var(--color-text-tertiary)',
                        }}>
                            {query.data.total} total
                        </span>
                    )}
                </div>

                {query.isLoading && (
                    <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>
                )}
                {query.isError && (
                    <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>
                        Could not load screenshots.
                    </p>
                )}
                {query.isSuccess && (!query.data?.days || query.data.days.length === 0) && (
                    <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                        No screenshots in this range. Captures only happen while you
                        are checked in via the web app.
                    </p>
                )}

                {query.isSuccess && query.data?.days?.length > 0 && (
                    <div className="flex flex-col" style={{ gap: 14 }}>
                        {query.data.days.map((d) => (
                            <ScreenshotDay key={d.date} day={d} onOpen={openAt} />
                        ))}
                    </div>
                )}
            </div>

            <ScreenshotLightbox
                open={lightbox.open}
                screenshots={flat}
                currentIndex={lightbox.flatIndex}
                onClose={() => setLightbox({ open: false, flatIndex: 0 })}
                onNavigate={(i) => setLightbox({ open: true, flatIndex: i })}
            />
        </div>
    );
}

function RangeChip({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            style={{
                padding: '5px 11px',
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 'var(--border-radius-md)',
                cursor: 'pointer',
                border: '0.5px solid var(--color-border-tertiary)',
                background: active ? 'var(--color-bg-info)' : 'var(--color-bg-primary)',
                color: active ? 'var(--color-text-info)' : 'var(--color-text-secondary)',
                borderColor: active ? 'var(--color-border-info)' : 'var(--color-border-tertiary)',
                transition: 'background-color 120ms ease, border-color 120ms ease',
            }}
        >
            {children}
        </button>
    );
}

function ScreenshotDay({ day, onOpen }) {
    const dt = DateTime.fromISO(day.date);
    const heading = `${dt.toFormat('EEEE')}, ${dt.toFormat('d LLL')}`;
    return (
        <div>
            <div
                style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                }}
            >
                {heading} · {day.screenshots.length} screenshot{day.screenshots.length !== 1 ? 's' : ''}
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 8,
                }}
            >
                {day.screenshots.map((s) => (
                    <ScreenshotThumb key={s.id} shot={s} onOpen={onOpen} />
                ))}
            </div>
        </div>
    );
}

function ScreenshotThumb({ shot, onOpen }) {
    const dt = DateTime.fromISO(shot.timestamp).setZone('Asia/Karachi');
    return (
        <button
            onClick={() => onOpen(shot.id)}
            style={{
                background: 'var(--color-bg-secondary)',
                border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 'var(--border-radius-md)',
                padding: 0,
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
            }}
            title={`${shot.active_app || '(no app)'} · ${dt.toFormat('HH:mm:ss')}`}
        >
            <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#000' }}>
                <AuthenticatedImage
                    screenshotId={shot.id}
                    alt={shot.active_app}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            </div>
            <div style={{ padding: '6px 8px', fontSize: 11 }}>
                <div className="tnum" style={{
                    color: 'var(--color-text-primary)',
                    fontWeight: 500,
                }}>
                    {dt.toFormat('HH:mm:ss')}
                </div>
                <div style={{
                    color: 'var(--color-text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {shot.active_app || '—'}
                </div>
            </div>
        </button>
    );
}
